import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { getValue } from 'remote-config-value'
import { makeStyles } from '@material-ui/core/styles'
import { ConfirmationDialog } from 'functional-foundations'
import { Typography } from 'foundations-library/components'
import { maxlength, minlength, regex, required } from 'validation-rules'
import { getUsecases } from './Usage'
import StepTitle from '../../shared/StepTitle'
import InfoSection from '../../shared/InfoSection'
import useFlatForm from '../../../hooks/useFlatForm'
import useDisableInput from '../../../hooks/useDisableInput'
import TextareaWithTitles from '../../shared/TextareaWithTitles'
import {
    getOtherUseCaseTextValue,
    useCasesOptions,
    replacePlaceholderWithActualBrandValues,
    isStringFreeOfPopularUrlShorteners
} from '../../../utils/util'

const MIN_MESSAGE_LENGTH = 20
const MAX_MESSAGE_LENGTH = 1024
const BRAND_NAME_ERROR = 'Your message has to include brand name'

const isFreeOfPopularUrShorteners = () => (val) => {
    if (isStringFreeOfPopularUrlShorteners(val)) return true

    return 'Value can\'t contain url shortners'
}

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: 30
    },
    mb5: {
        marginBottom: 5
    },
    modifyWarning: {
        marginTop: -30,
        marginBottom: 40
    },
    textCenter: {
        textAlign: 'center'
    }
}))

const textValues = JSON.parse(getValue('sms_enrollment_campaigns_2nd_step'))

const normalizeValidationText = (value = '') => {
    return value
        .toLowerCase()
        .replace(/[^a-z0-9]+/gi, ' ')
        .trim()
        .replace(/\s+/g, ' ')
}

const containsBrandName = (brandName = '') => (value) => {
    if (!brandName || !value || typeof value !== 'string') return true

    const normalizedBrandName = normalizeValidationText(brandName)
    const normalizedValue = normalizeValidationText(value)

    if (!normalizedBrandName) return true

    return ` ${normalizedValue} `.includes(` ${normalizedBrandName} `)
        ? true
        : BRAND_NAME_ERROR
}

/**
 *
 */
export const generateSampleMessages = (
    useCases = [],
    wizardData = {},
    editing = null,
    brand
) => {
    const dataProps = ['samplesms1', 'samplesms2', 'samplesms3', 'samplesms4', 'samplesms5']
    if (editing) { // usecases are disabled just return whatever we have
        return dataProps.reduce((a, c) => {
            a[c] = {
                value: wizardData?.[c]?.value || editing[c],
                forUseCase: Boolean(editing[c])
            }

            return a
        }, {})
    }

    if (!useCases.length) {
        return dataProps.reduce((a, c) => {
            a[c] = {
                value: '',
                forUseCase: null
            }

            return a
        }, {})
    }

    const wData = dataProps.map((c) => wizardData?.[c] || null).filter(Boolean)

    const otherValue = getOtherUseCaseTextValue(useCases)

    return dataProps.reduce((data, prop, i) => {
        const uc = useCases[i]
        if (!useCases[i]) {
            data[prop] = {
                value: '',
                forUseCase: null
            }
        } else if (wData.find((value) => value.forUseCase === uc)) {
            const dedicatedSampleMessage = wData.find((value) => value.forUseCase === uc)
            data[prop] = dedicatedSampleMessage
        } else if (uc === otherValue) {
            data[prop] = {
                forUseCase: 'OTHER',
                value:
                    (wData.find((x) => x.forUseCase === 'OTHER'))?.value ||
                    replacePlaceholderWithActualBrandValues(textValues.OTHER_sample_message, brand)
            }
        } else {
            const message = (useCasesOptions.find((x) => x.id === uc && x.sampleMessage)).sampleMessage
            data[prop] = {
                forUseCase: uc,
                value: replacePlaceholderWithActualBrandValues(message, brand)
            }
        }

        return data
    }, {})
}

/**
 * @param {object} props
 */
const SampleMessages = (props) => {
    const smsContentData = props.passData.campaign_sms_content
    const brand = props.passData?.campaign_usage?.selectedBrand

    const { editing } = props
    const brandNameRule = containsBrandName(brand?.name || editing?.brand_name || '')
    const useCases = getUsecases(props.passData?.campaign_usage, editing)
    const sampleMessages = generateSampleMessages(useCases, smsContentData, editing, brand)
    const { disableAllInputs } = useDisableInput({ mode: props.mode, item: props.editing })

    const {
        data,
        onClear,
        setDataProp,
        setIsValidProp,
        isFormDataValid
    } = useFlatForm({
        helpMessage:
            smsContentData?.helpMessage ||
            (editing != null ? editing.keywords.help.CampaignkeywordValue : '') ||
            replacePlaceholderWithActualBrandValues(textValues.help_response_value, brand),
        optInMessage:
            smsContentData?.optInMessage ||
            (editing != null ? editing.keywords.start.CampaignkeywordValue : '') ||
            replacePlaceholderWithActualBrandValues(textValues.first_message_opted_in_value, brand, useCases),
        optOutMessage:
            smsContentData?.optOutMessage ||
            (editing != null ? editing.keywords.stop.CampaignkeywordValue : '') ||
            replacePlaceholderWithActualBrandValues(textValues.stop_response_value, brand),
        ...(sampleMessages)
    }, {
        samplesms1: !sampleMessages.samplesms1.value,
        samplesms2: !sampleMessages.samplesms2.value,
        samplesms3: !sampleMessages.samplesms3.value,
        samplesms4: !sampleMessages.samplesms4.value,
        samplesms5: !sampleMessages.samplesms5.value
    })

    const classes = useStyles()
    const [showConfirmModal, setShowConfirmModal] = useState(false)

    const gotoNextStep = () => {
        setShowConfirmModal(false)
        props.update?.({ completed: true, gotoNext: true, saveData: data })
    }

    const onRejectHandler = () => {
        setShowConfirmModal(false)
    }

    useEffect(() => {
        if (props.saveId) {
            if (props.gotoStepIndex > props.stepIndex) {
                setShowConfirmModal(true)
            } else {
                gotoNextStep()
            }
        }
    }, [props.saveId, props.gotoStepIndex, props.stepIndex])

    useEffect(() => {
        props.update?.({ disableNextButton: () => !isFormDataValid })
    }, [isFormDataValid])

    const onSampleChange = (prop) => (val) => {
        return setDataProp(prop)({
            ...data[prop],
            value: val
        })
    }

    return <div data-testid='s-messages'>
        <StepTitle remoteConfigID='sms_enrollment_sample_message' data-testid='sample-messages-step-title' />
        {
            !disableAllInputs &&
            <InfoSection className={classes.modifyWarning}>
                <Typography variant='subtitle2'>{textValues.sample_messages_modify_warning}</Typography>
            </InfoSection>
        }
        <div className={classes.container}>
            <div>
                <Typography variant='subtitle1' className={classes.mb5} data-testid='s-messages-general'>
                    General messages
                </Typography>
                <Typography variant='body1' data-testid='s-messages-prepopulated'>
                    These pre-populated examples fulfill industry requirements. You may leave them as-is or edit them as you wish.
                </Typography>
            </div>
            <TextareaWithTitles
                value={data.helpMessage}
                onChange={setDataProp('helpMessage')}
                onClear={onClear('helpMessage')}
                isValid={setIsValidProp('helpMessage')}
                rules={[
                    required(),
                    brandNameRule,
                    regex(new RegExp('STOP'), undefined, 'Your message has to include word \'STOP\''),
                    minlength(MIN_MESSAGE_LENGTH),
                    maxlength(MAX_MESSAGE_LENGTH),
                    isFreeOfPopularUrShorteners()
                ]}
                title={textValues.help_response_title}
                subtitle={textValues.help_response_subtitle}
                placeholder={textValues.first_message_opted_in_placeholder}
                minHeight={94}
                disabled={disableAllInputs}
                data-testid='s-messages-help'
            />
            <TextareaWithTitles
                value={data.optInMessage}
                onChange={setDataProp('optInMessage')}
                onClear={onClear('optInMessage')}
                isValid={setIsValidProp('optInMessage')}
                rules={[
                    required(),
                    brandNameRule,
                    regex(new RegExp('STOP'), undefined, 'Your message has to include word \'STOP\''),
                    regex(new RegExp('HELP'), undefined, 'Your message has to include word \'HELP\''),
                    minlength(MIN_MESSAGE_LENGTH),
                    maxlength(MAX_MESSAGE_LENGTH),
                    isFreeOfPopularUrShorteners()
                ]}
                title={textValues.first_message_opted_in}
                subtitle={textValues.first_message_opted_in_subtitle}
                placeholder={textValues.help_response_placeholder}
                disabled={disableAllInputs}
                data-testid='s-messages-opt-in'
            />
            <TextareaWithTitles
                value={data.optOutMessage}
                onChange={setDataProp('optOutMessage')}
                onClear={onClear('optOutMessage')}
                isValid={setIsValidProp('optOutMessage')}
                rules={[
                    required(),
                    brandNameRule,
                    regex(new RegExp('START'), undefined, 'Your message has to include word \'START\''),
                    minlength(MIN_MESSAGE_LENGTH),
                    maxlength(MAX_MESSAGE_LENGTH),
                    isFreeOfPopularUrShorteners()
                ]}
                title={textValues.stop_response_title}
                subtitle={textValues.stop_response_subtitle}
                placeholder={textValues.stop_response_placeholder}
                disabled={disableAllInputs}
                data-testid='s-messages-opt-out'
            />
            <div>
                <Typography variant='subtitle1' className={classes.mb5} data-testid='s-messages-c-messages-title'>
                    Campaign messages
                </Typography>
                <Typography variant='body2'>
                    These should be specific to your business/use case and accurately reflect the messages that you’ll be sending. They must also include the brand name, “STOP” and “HELP” language. See examples at the bottom of the page for reference.
                </Typography>
            </div>
            <div className={classes.container}>
                {
                    Object.entries(sampleMessages).map(([prop, conf], i) => {
                        if (conf.forUseCase) {
                            return <TextareaWithTitles
                                value={data[prop].value}
                                onChange={onSampleChange(prop)}
                                onClear={() => setDataProp(prop)({
                                    ...conf,
                                    value: ''
                                })}
                                isValid={setIsValidProp(prop)}
                                rules={
                                    [
                                        required(),
                                        brandNameRule,
                                        regex(new RegExp('STOP'), undefined, 'Your message has to include word \'STOP\''),
                                        regex(new RegExp('HELP'), undefined, 'Your message has to include word \'HELP\''),
                                        minlength(MIN_MESSAGE_LENGTH),
                                        maxlength(MAX_MESSAGE_LENGTH),
                                        isFreeOfPopularUrShorteners()
                                    ]
                                }
                                title={
                                    `
                                    ${textValues.sample_message_title} 
                                    (${i + 1} of ${Object.values(sampleMessages).filter((x) => x.forUseCase).length})
                                    `
                                }
                                subtitle={textValues.sample_message_subtitle}
                                placeholder={textValues.sample_message_placeholder}
                                key={prop}
                                disabled={disableAllInputs}
                                data-testid={`s-messages-c-message-${i}`}
                            />
                        }
                        return <></>
                    })
                }
            </div>
        </div>
        <ConfirmationDialog
            width={600}
            title='Confirmation'
            isOpen={showConfirmModal}
            onConfirm={gotoNextStep}
            onCancel={onRejectHandler}
            confirmText='Confirm and continue'
            cancelText='Edit'
            cancelProps={{ variant: 'outlined' }}
            confirmProps={{ icon: 'go' }}
            className={classes.textCenter}
        >
            <div dangerouslySetInnerHTML={{ __html: getValue('sms_enrollment_please_confirm_campaign_registration') }} />
        </ConfirmationDialog>
    </div>
}

SampleMessages.propTypes = {
    passData: PropTypes.object,
    update: PropTypes.func,
    saveId: PropTypes.number,
    gotoStepIndex: PropTypes.number,
    stepIndex: PropTypes.number,
    editing: PropTypes.object,
    showTitle: PropTypes.bool,
    showContainer: PropTypes.bool,
    mode: PropTypes.string
}

export default SampleMessages
